import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Bell, Cake, UserX, CalendarClock, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useWaMsg } from "@/lib/waTemplates";
import { useNavigate } from "react-router-dom";
import { OilReminders } from "@/components/OilReminders";
import { PartReminders } from "@/components/PartReminders";

const digits = (s) => (s || "").replace(/\D/g, "");

function waLink(phone, msg) {
  const d = digits(phone);
  if (!d) return "";
  const wa = d.length >= 12 ? d : `55${d}`;
  return `https://wa.me/${wa}?text=${encodeURIComponent(msg)}`;
}

const TYPE_META = {
  revision: { icon: CalendarClock, label: "Revisão próxima", tone: "amber", testid: "rem-revision" },
  birthday_today: { icon: Cake, label: "Aniversário hoje", tone: "pink", testid: "rem-bday-today" },
  birthday_week: { icon: Cake, label: "Aniversário essa semana", tone: "pink", testid: "rem-bday-week" },
  lost: { icon: UserX, label: "Cliente sumido", tone: "red", testid: "rem-lost" },
  follow_up: { icon: CalendarClock, label: "Retornar orçamento", tone: "amber", testid: "rem-follow-up" },
};

function FollowUpSection({ items, shopName, onSchedule }) {
  const waMsg = useWaMsg();
  if (!items?.length) return null;
  return (
    <section data-testid="rem-follow-up" className="rounded-2xl border border-card2-border bg-card2 p-4">
      <div className="flex items-center gap-2 mb-3 text-xs font-mono-caps text-zinc-400 uppercase">
        <CalendarClock className="h-4 w-4" /> Retornar orçamento <span className="text-zinc-600">· {items.length}</span>
      </div>
      <ul className="space-y-2">
        {items.map((it, idx) => {
          const vars = { cliente: it.customer_name || "", placa: it.vehicle_plate || "", oficina: shopName, data: it.when ? new Date(it.when + "T00:00:00").toLocaleDateString("pt-BR") : "" };
          const defaultMsg = waMsg("follow_up", vars) || `Oi ${it.customer_name || ""}, tudo bem? Lembrando do orçamento que combinamos de retomar hoje${it.note ? ` (${it.note})` : ""}. Posso agendar?`;
          const href = waLink(it.phone, defaultMsg);
          return (
            <li key={`fu-${it.order_id}-${idx}`} data-testid={`rem-follow-up-item-${it.order_id}-${idx}`} className="flex items-start gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
              <div className="flex-1 min-w-0">
                <Link to={`/owner/customers/${it.customer_id}/crm`} className="text-sm font-bold text-zinc-100 hover:text-green-neon">{it.customer_name || "—"}</Link>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {it.phone || "sem telefone"}
                  {it.vehicle_plate && <> · <span className="font-mono text-zinc-400">{it.vehicle_plate}</span></>}
                  {it.order_number != null && <> · OS #{it.order_number}</>}
                  {it.scope === "item" && it.item_description && <> · item “{it.item_description}”</>}
                  {it.note && <> · <span className="text-amber-300">{it.note}</span></>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {href && <a data-testid={`rem-follow-up-wa-${it.order_id}-${idx}`} href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 h-8 px-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>}
                {onSchedule && <Button data-testid={`rem-follow-up-schedule-${it.order_id}-${idx}`} size="sm" onClick={() => onSchedule(it)} className="h-8 bg-amber-brand hover:bg-amber-brandDark text-black font-bold"><CalendarClock className="h-3.5 w-3.5 mr-1" /> Agendar</Button>}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Section({ type, items, shopName, onContacted, onSchedule }) {
  const meta = TYPE_META[type];
  const Icon = meta.icon;
  const waMsg = useWaMsg();
  if (!items?.length) return null;
  return (
    <section data-testid={meta.testid} className={`rounded-2xl border border-card2-border bg-card2 p-4`}>
      <div className="flex items-center gap-2 mb-3 text-xs font-mono-caps text-zinc-400 uppercase">
        <Icon className="h-4 w-4" /> {meta.label} <span className="text-zinc-600">· {items.length}</span>
      </div>
      <ul className="space-y-2">
        {items.map((it) => {
          let defaultMsg = "";
          const vars = { cliente: it.customer_name || "", placa: it.vehicle_plate || "", oficina: shopName,
            data: it.next_date ? new Date(it.next_date + "T00:00:00").toLocaleDateString("pt-BR") : "breve" };
          if (type === "revision") defaultMsg = waMsg("revision", vars);
          else if (type === "birthday_today" || type === "birthday_week") defaultMsg = waMsg("birthday", vars);
          else defaultMsg = waMsg("lost", vars);
          const href = waLink(it.phone, defaultMsg);
          return (
            <li key={`${type}-${it.customer_id}-${it.order_id || ""}`} data-testid={`${meta.testid}-item-${it.customer_id}`} className="flex items-start gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
              <div className="flex-1 min-w-0">
                <Link to={`/owner/customers/${it.customer_id}/crm`} className="text-sm font-bold text-zinc-100 hover:text-green-neon">{it.customer_name || "—"}</Link>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {it.phone || "sem telefone"}
                  {type === "revision" && it.vehicle_plate && <> · <span className="font-mono text-zinc-400">{it.vehicle_plate}</span></>}
                  {type === "lost" && it.days != null && <> · {it.days} dias sem vir</>}
                  {it.last_contacted_at && <> · <span className="text-amber-300">contatado em {new Date(it.last_contacted_at).toLocaleDateString("pt-BR")}</span></>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {href && (
                  <a data-testid={`${meta.testid}-wa-${it.customer_id}`} href={href} target="_blank" rel="noopener noreferrer" title="Abrir WhatsApp" className="inline-flex items-center gap-1 h-8 px-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
                )}
                {onSchedule && (type === "revision" || type === "lost") && (
                  <Button data-testid={`${meta.testid}-schedule-${it.customer_id}`} size="sm" onClick={() => onSchedule(it)} className="h-8 bg-amber-brand hover:bg-amber-brandDark text-black font-bold"><CalendarClock className="h-3.5 w-3.5 mr-1" /> Agendar</Button>
                )}
                <Button data-testid={`${meta.testid}-mark-${it.customer_id}`} size="sm" variant="outline" onClick={() => onContacted(type, it)} className="h-8 bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800"><Check className="h-3.5 w-3.5 mr-1" /> Contatei</Button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function OwnerReminders() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parts, setParts] = useState(0);
  const navigate = useNavigate();
  const openSchedule = (it) => navigate("/owner/agendamentos", { state: { prefill: { customer: { id: it.customer_id, name: it.customer_name }, plate: it.vehicle_plate || "" } } });
  const shopName = user?.shop?.name || "nossa oficina";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/reminders");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markContacted = async (type, item) => {
    try {
      await api.post("/crm/reminders/log", { type, customer_id: item.customer_id, order_id: item.order_id || null });
      toast.success("Marcado como contatado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const total = useMemo(() => {
    if (!data) return 0;
    return (data.revisions?.length || 0) + (data.birthdays_today?.length || 0) + (data.birthdays_week?.length || 0) + (data.lost?.length || 0) + (data.follow_ups?.length || 0) + parts;
  }, [data, parts]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-green-brand" /></div>;

  return (
    <div className="space-y-5 animate-fade-up">
      <div>
        <div className="flex items-center gap-2 text-xs font-mono-caps text-zinc-500 uppercase"><Bell className="h-4 w-4" /> Lembretes</div>
        <h1 className="font-display text-3xl font-black uppercase text-zinc-100 mt-1">Lembretes de retorno</h1>
        <p className="text-sm text-zinc-400 mt-1">{total} contato(s) sugerido(s) hoje — mensagem pronta por tipo, você edita antes de enviar.</p>
      </div>
      <Section type="revision" items={data?.revisions || []} shopName={shopName} onContacted={markContacted} onSchedule={openSchedule} />
      <FollowUpSection items={data?.follow_ups || []} shopName={shopName} onSchedule={openSchedule} />
      <Section type="birthday_today" items={data?.birthdays_today || []} shopName={shopName} onContacted={markContacted} />
      <Section type="birthday_week" items={data?.birthdays_week || []} shopName={shopName} onContacted={markContacted} />
      <Section type="lost" items={data?.lost || []} shopName={shopName} onContacted={markContacted} onSchedule={openSchedule} />
      <PartReminders onCount={setParts} />
      <OilReminders />
      {total === 0 && (
        <div className="rounded-2xl border border-dashed border-card2-border bg-card2/40 p-10 text-center">
          <Bell className="h-10 w-10 mx-auto text-zinc-700" />
          <p className="mt-3 text-zinc-400">Nenhum lembrete pendente agora. Aproveita e cadastra uma nova OS 😉</p>
        </div>
      )}
    </div>
  );
}
